import { FetchablePaginatedEntityType } from '@/entityType';
import { TryFetchPaginatedEntitiesAction } from '@/actions/entities';
import {
    IssuesRequest,
    SET_TRIAGE_DESTINATIONS,
    SET_TRIAGE_FILTERS,
    SET_TRIAGE_INSIGHTS,
    SET_TRIAGE_OWNERS,
    SET_TRIAGE_RELEASE_FILTER,
    SET_TRIAGE_REPORTERS,
    SET_TRIAGE_SEARCH,
    SET_TRIAGE_SORT_BY,
    SET_TRIAGE_SOURCES,
    SET_TRIAGE_STATUS_GROUP,
    SET_TRIAGE_STATUSES,
    SET_TRIAGE_VERSIONS,
    TriageFilterValues,
    TriageFiltersAction,
} from '@/actions/triage';

export interface TriageFiltersState extends TriageFilterValues {
    lastQuery?: IssuesRequest;
}

const defaultFilters: TriageFiltersState = {
    statusGroup: 'open',
    statuses: [],
    sources: [],
    owners: [],
    reporters: [],
    versions: [],
    destinations: [],
    insights: [],
    sortBy: 'triage',
    releaseFilter: 'none',
    search: '',
};

// Keyed by appId so each app keeps its own filter state.
export type TriageFiltersByAppId = Record<string, TriageFiltersState>;

const filtersForApp = (state: TriageFiltersByAppId, appId: string): TriageFiltersState =>
    state[appId] ?? defaultFilters;

export function triageFiltersReducer(
    state: TriageFiltersByAppId = {},
    action: TriageFiltersAction | TryFetchPaginatedEntitiesAction,
): TriageFiltersByAppId {
    switch (action.type) {
        case FetchablePaginatedEntityType.Issues: {
            const query = action.payload.data as IssuesRequest;
            return {
                ...state,
                [query.appId]: {
                    ...filtersForApp(state, query.appId),
                    lastQuery: query,
                },
            };
        }
        case SET_TRIAGE_STATUS_GROUP: {
            const current = filtersForApp(state, action.payload.appId);
            return {
                ...state,
                [action.payload.appId]: {
                    ...current,
                    statusGroup: action.payload.statusGroup,
                    // clear status filter only when changing bucket
                    statuses: current.statusGroup === action.payload.statusGroup ? current.statuses : [],
                },
            };
        }
        case SET_TRIAGE_STATUSES: {
            return {
                ...state,
                [action.payload.appId]: {
                    ...filtersForApp(state, action.payload.appId),
                    statuses: action.payload.statuses,
                },
            };
        }
        case SET_TRIAGE_SOURCES: {
            return {
                ...state,
                [action.payload.appId]: {
                    ...filtersForApp(state, action.payload.appId),
                    sources: action.payload.sources,
                },
            };
        }
        case SET_TRIAGE_OWNERS: {
            return {
                ...state,
                [action.payload.appId]: {
                    ...filtersForApp(state, action.payload.appId),
                    owners: action.payload.owners,
                },
            };
        }
        case SET_TRIAGE_REPORTERS: {
            return {
                ...state,
                [action.payload.appId]: {
                    ...filtersForApp(state, action.payload.appId),
                    reporters: action.payload.reporters,
                },
            };
        }
        case SET_TRIAGE_VERSIONS: {
            return {
                ...state,
                [action.payload.appId]: {
                    ...filtersForApp(state, action.payload.appId),
                    versions: action.payload.versions,
                },
            };
        }
        case SET_TRIAGE_DESTINATIONS: {
            return {
                ...state,
                [action.payload.appId]: {
                    ...filtersForApp(state, action.payload.appId),
                    destinations: action.payload.destinations,
                },
            };
        }
        case SET_TRIAGE_INSIGHTS: {
            return {
                ...state,
                [action.payload.appId]: {
                    ...filtersForApp(state, action.payload.appId),
                    insights: action.payload.insights,
                },
            };
        }
        case SET_TRIAGE_SORT_BY: {
            return {
                ...state,
                [action.payload.appId]: {
                    ...filtersForApp(state, action.payload.appId),
                    sortBy: action.payload.sortBy,
                },
            };
        }
        case SET_TRIAGE_RELEASE_FILTER: {
            return {
                ...state,
                [action.payload.appId]: {
                    ...filtersForApp(state, action.payload.appId),
                    releaseFilter: action.payload.releaseFilter,
                },
            };
        }
        case SET_TRIAGE_SEARCH: {
            return {
                ...state,
                [action.payload.appId]: {
                    ...filtersForApp(state, action.payload.appId),
                    search: action.payload.search,
                },
            };
        }
        case SET_TRIAGE_FILTERS: {
            return {
                ...state,
                [action.payload.appId]: {
                    ...filtersForApp(state, action.payload.appId),
                    ...action.payload.filters,
                },
            };
        }
        default:
            return state;
    }
}

export { defaultFilters as DEFAULT_TRIAGE_FILTERS };
export default triageFiltersReducer;
